import React from 'react';
import { Box, BoxProps } from '@mui/material';

import { PageWidth, useLayoutTokens } from '../hooks/useLayoutTokens';

interface PageShellProps extends Omit<BoxProps, 'maxWidth'> {
  maxWidth?: PageWidth;
}

// Shared page container. Both of its horizontal dimensions — the side padding
// and the content cap — are driven by the user's Display & Layout preference
// (see hooks/useLayoutTokens). Density-driven tokens (grid spacing, card/panel
// padding, etc.) are read from useLayoutTokens by the pages themselves rather
// than exported statically from here.
//
// The cap matters more than the padding: at 'standard' it is a fixed pixel
// value with mx:'auto', so on a display wider than the cap the leftover space
// becomes margin. Edge-to-edge resolves the cap to '100%' precisely so that
// margin disappears instead of merely shrinking the 8px gutter beside it.
const PageShell: React.FC<PageShellProps> = ({
  maxWidth = 'standard',
  sx,
  children,
  ...boxProps
}) => {
  const { pageSidePaddingX, pageMaxWidth } = useLayoutTokens();
  // Normalize the optional sx into a spreadable array; `false` is a valid,
  // ignored sx entry, keeping the undefined case type-safe.
  const sxOverrides = sx ?? false;

  return (
    <Box
      {...boxProps}
      sx={[
        {
          width: '100%',
          maxWidth: pageMaxWidth[maxWidth],
          mx: 'auto',
          px: pageSidePaddingX,
        },
        ...(Array.isArray(sxOverrides) ? sxOverrides : [sxOverrides]),
      ]}
    >
      {children}
    </Box>
  );
};

export default PageShell;
